import { useEffect, useState } from "react";
import "./AlmostDone.scss";

import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router";

import { Button } from "@thrivetribe/gloji.ui.atoms.button";
import {
  ModalPageHeader,
  ModalPageBody,
  ModalPageFooter,
  ModalPageWrapper,
  ModalPageHeading,
  ModalPageCopy,
} from "../../../organisms/ModalPage";
import { mapSignupFormValues } from "../helpers";
import Toast from "../../../atoms/Toast";
import authChallenge from "../../../auth/AuthChallenge";
import { ProgrammeProps } from "../Props";
import { pageTitleSuffix } from "../../../../data/eligibilityChecker";
import Pathway from "../../../../enums/profile/Pathway";
import useUser from "../../../hooks/useUser";
import { useCreateSelfReferralMutation } from "../../../../redux/referralApiV5";
import { useAddJourneyToProfileMutation } from "../../../../redux/profileApiV2";
import { CustomModal, ModalContent, ModalTitle } from "../../../organisms/CustomModal";
import { PowerOfTen, ProgressStepEnum, setStepProgress } from "../../../../redux/slices/progressBarSlice";

export interface AlmostDoneProps extends ProgrammeProps {
  programmeSlug: string;
  profileBuilderUrl: string;
  progressPercentage?: PowerOfTen;
}

export function AlmostDone({
  programmeTitle,
  programmeSlug,
  profileBuilderUrl,
  progressPercentage,
}: Readonly<AlmostDoneProps>) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [newReferralCode, setNewReferralCode] = useState(null);
  const [retryJourneyModalOpen, setRetryJourneyModalOpen] = useState<boolean>(false);

  const [user] = useUser();
  const history = useHistory();
  let { path } = useRouteMatch();

  const dispatch = useDispatch();

  useEffect(() => {
    if (progressPercentage) {
      dispatch(
        setStepProgress({
          progressStepEnum: ProgressStepEnum.CheckEligiblity,
          progressPercentage: progressPercentage,
        })
      );
    }
  }, [dispatch, progressPercentage]);

  const signupFormValues = useSelector(
    (state: { signupFlowData: { signupFormValues: any } }) => state.signupFlowData.signupFormValues
  );
  const { email: otpEmail, isVerified: otpIsVerified } = useSelector(
    (state: { otp: { email: string; isVerified: boolean } }) => state.otp
  );
  const [createSelfReferral] = useCreateSelfReferralMutation();
  const [addJourneyToProfile] = useAddJourneyToProfileMutation();

  const pageTitle = `Almost Done | ${programmeTitle} | ${pageTitleSuffix}`;

  useEffect(() => {
    window.gtag("event", "page_view", {
      page_title: pageTitle,
    });
  }, []);

  const handleSubmit = async () => {
    let isSuccessfulSignUp = true;
    setIsSubmitting(true);

    //Trigger google analytics event on end of eligibility checker journey
    window.gtag("event", "completeEligibilityChecker", {
      event_category: "Eligibility Checker",
      event_label: "completeEligibilityChecker",
    });

    window.dataLayer.push({
      event: "c_events",
      event_category: "Signup",
      event_action: "Eligibility Checker Complete",
    });

    const formValues = mapSignupFormValues(programmeSlug, {
      ...signupFormValues,
      ...{ email: user ? user.emails[0] : otpEmail },
      ...{ emailVerified: !!user || otpIsVerified },
      ...{ preferences: ["email"] },
      ...(programmeSlug === "smoke-free"
        ? { pathway: Pathway.SUPPORTED }
        : { pathway: signupFormValues.pathway }),
    });
    setErrorMessage("");
    try {
      await createSelfReferral(formValues)
        .unwrap()
        .then(async referralResponse => {
          setNewReferralCode(referralResponse.referralCode);
          if (user) {
            addJourney(referralResponse.referralCode);
          } else {
            try {
              let queryParams = {
                login_hint: otpEmail,
                referral_code: referralResponse.referralCode,
              };

              await authChallenge.signUp(queryParams);
            } catch (error) {
              isSuccessfulSignUp = false;
            }
          }
        });
    } catch (error) {
      setErrorMessage(error?.message);
    }
    if (!isSuccessfulSignUp) {
      history.push(`${path}/error`);
    }
  };

  const addJourney = async (referralCode?: string) => {
    setRetryJourneyModalOpen(false);
    try {
      await addJourneyToProfile({
        userId: user?.sub ?? "",
        referralCode: referralCode ?? newReferralCode,
      })
        .unwrap()
        .then(async response => {
          window.location.replace(`${profileBuilderUrl}/${programmeSlug}`);
        });
    } catch (error) {
      if (error.status === 504 || error.status === 503 || error.status === 408) {
        // retry button
        setRetryJourneyModalOpen(true);
      }
      console.log("journey create error: ", error);
      history.push(`${path}/error`);
    }
  };

  return (
    <>
      <Toast show={Boolean(errorMessage)} type="error">
        {errorMessage}
      </Toast>

      <CustomModal
        open={retryJourneyModalOpen}
        id="retryAddJourneyModal"
        modalTitleId="retryAddJourneyModalTitle"
        modalDescriptionId="retryAddJourneyModalDescription"
        aria-labelledby={"retryAddJourneyModalTitle"}
      >
        <ModalContent id="retryAddJourneyModalContent">
          <ModalTitle id="retryAddJourneyModalTitle">Partial success</ModalTitle>
          <p className="modal-text" id="retryAddJourneyModalDescription">
            Part of the request has failed, please try again.
          </p>
          <div id="retryAddJourneyModalButtons">
            <Button className="modal-button" variant="contained" color="secondary" onClick={addJourney}>
              Retry
            </Button>
          </div>
        </ModalContent>
      </CustomModal>

      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <ModalPageWrapper>
        <ModalPageBody className="with-sticky-footer">
          <ModalPageHeader
            backButtonOnClick={() => {
              history.goBack();
            }}
          />

          <ModalPageHeading>Almost done</ModalPageHeading>
          <ModalPageCopy>
            <span style={{ marginTop: "15px", marginBottom: "20px" }}>
              Just a few more questions to check your details are all up-to-date and in order.
            </span>
          </ModalPageCopy>
        </ModalPageBody>
        <ModalPageFooter className="sticky-footer">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            onClick={handleSubmit}
          >
            {user ? "Continue" : "Create account"}
          </Button>
        </ModalPageFooter>
      </ModalPageWrapper>
    </>
  );
}

export default AlmostDone;
